import React from "react";
import { useBluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import Message from "../components/Message";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";

const TEMPLATE_CATEGORIES = [
    { value: "AUTHENTICATION", label: "Authentication" },
    { value: "MARKETING", label: "Marketing" },
    { value: "UTILITY", label: "Utility" },
];

const COMPONENT_TYPES = [
    { value: "HEADER", label: "Header" },
    { value: "BODY", label: "Body" },
    { value: "FOOTER", label: "Footer" },
    { value: "BUTTONS", label: "Buttons" },
];

export function WhatsAppTemplateModal({ integration, onClose, onSuccess }) {
    const [step, setStep] = React.useState("list");
    const [templates, setTemplates] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [newTemplate, setNewTemplate] = React.useState({
        name: "",
        category: "UTILITY",
        language: "en",
        components: [
            {
                type: "BODY",
                text: "",
                example: {
                    body_text: [[""]]
                }
            }
        ]
    });

    const { call: getTemplates } = useBluelagoon({
        method: "getWhatsAppTemplates",
    });

    const { call: createTemplate } = useBluelagoon({
        method: "createWhatsAppTemplate",
    });

    const { call: getTemplateStatus } = useBluelagoon({
        method: "getWhatsAppTemplateStatus",
    });

    React.useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const response = await getTemplates({ integrationId: integration.id });
            if (response?.data?.templates) {
                setTemplates(response.data.templates);
            }
        } catch (error) {
            console.error("Error loading templates:", error);
            toast.error("Failed to load templates");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        if (!newTemplate.name || !newTemplate.components[0]?.text) {
            toast.error("Please fill in all required fields");
            return;
        }

        setIsLoading(true);
        try {
            const response = await createTemplate({
                integrationId: integration.id,
                template: newTemplate
            });

            if (response?.success) {
                toast.success("Template created successfully!");
                setStep("list");
                setNewTemplate({
                    name: "",
                    category: "UTILITY",
                    language: "en",
                    components: [
                        {
                            type: "BODY",
                            text: "",
                            example: {
                                body_text: [[""]]
                            }
                        }
                    ]
                });
                loadTemplates();
            } else {
                toast.error(response?.message || "Failed to create template");
            }
        } catch (error) {
            console.error("Error creating template:", error);
            toast.error("Failed to create template");
        } finally {
            setIsLoading(false);
        }
    };

    const checkTemplateStatus = async (templateName) => {
        try {
            const response = await getTemplateStatus({
                integrationId: integration.id,
                templateName
            });
            
            if (response?.data?.status) {
                toast.success(`Template status: ${response.data.status}`);
            }
        } catch (error) {
            console.error("Error checking template status:", error);
            toast.error("Failed to check template status");
        }
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            APPROVED: "bg-green-100 text-green-800",
            PENDING: "bg-yellow-100 text-yellow-800",
            REJECTED: "bg-red-100 text-red-800",
            DISABLED: "bg-gray-100 text-gray-800",
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.PENDING}`}>
                {status || 'PENDING'}
            </span>
        );
    };

    const updateComponentText = (index, text) => {
        const updatedComponents = [...newTemplate.components];
        updatedComponents[index] = {
            ...updatedComponents[index],
            text,
            example: updatedComponents[index].type === "BODY" ? {
                body_text: [[text]]
            } : undefined
        };
        setNewTemplate({ ...newTemplate, components: updatedComponents });
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-full max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <img
                            src="/icons/whatsapp.png"
                            alt="WhatsApp"
                            className="w-8 h-8"
                        />
                        WhatsApp Templates - {integration.name}
                    </DialogTitle>
                    <DialogDescription>
                        Manage your WhatsApp message templates
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4 py-2 px-1">
                    {/* Navigation */}
                    <div className="flex gap-2 border-b pb-2 overflow-x-auto">
                        <button
                            onClick={() => setStep("list")}
                            className={`px-3 py-2 rounded text-sm font-medium whitespace-nowrap flex-shrink-0 ${step === "list" ? "bg-blue-100 text-blue-800" : "text-gray-600 hover:bg-gray-100"}`}
                        >
                            Templates
                        </button>
                        <button
                            onClick={() => setStep("create")}
                            className={`px-3 py-2 rounded text-sm font-medium whitespace-nowrap flex-shrink-0 ${step === "create" ? "bg-blue-100 text-blue-800" : "text-gray-600 hover:bg-gray-100"}`}
                        >
                            Create New
                        </button>
                    </div>

                    {step === "list" && (
                        <div className="space-y-4">
                            {isLoading ? (
                                <div className="text-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-2 text-gray-600">Loading templates...</p>
                                </div>
                            ) : templates.length === 0 ? (
                                <Message>
                                    <div className="text-center py-4">
                                        <p>No templates found for this WhatsApp integration.</p>
                                        <button
                                            onClick={() => setStep("create")}
                                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Create Your First Template
                                        </button>
                                    </div>
                                </Message>
                            ) : (
                                <div className="space-y-3">
                                    {templates.map((template, index) => (
                                        <div key={template.name || index} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-medium">{template.name}</h4>
                                                    <p className="text-sm text-gray-600">Category: {template.category}</p>
                                                    <p className="text-sm text-gray-600">Language: {template.language}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {getStatusBadge(template.status)}
                                                    <button
                                                        onClick={() => checkTemplateStatus(template.name)}
                                                        className="p-1 text-gray-500 hover:text-gray-700"
                                                        title="Refresh status"
                                                    >
                                                        <Icon name="RefreshCw" className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {template.components && (
                                                <div className="mt-3">
                                                    <p className="text-sm font-medium text-gray-700 mb-1">Components:</p>
                                                    {template.components.map((component, compIndex) => (
                                                        <div key={compIndex} className="text-sm bg-gray-50 p-2 rounded mb-1">
                                                            <span className="font-medium">{component.type}:</span>{" "}
                                                            {component.text || component.format || "N/A"}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {step === "create" && (
                        <div className="space-y-4">
                            <Message>
                                <p className="text-sm">
                                    Create a new WhatsApp message template. Templates must be approved by WhatsApp before they can be used.
                                </p>
                            </Message>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Template Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={newTemplate.name}
                                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                                        placeholder="e.g., order_confirmation"
                                        className="w-full p-2 border rounded-md text-sm"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        Category *
                                    </label>
                                    <select
                                        value={newTemplate.category}
                                        onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                                        className="w-full p-2 border rounded-md text-sm"
                                    >
                                        {TEMPLATE_CATEGORIES.map((cat) => (
                                            <option key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Language
                                </label>
                                <input
                                    type="text"
                                    value={newTemplate.language}
                                    onChange={(e) => setNewTemplate({ ...newTemplate, language: e.target.value })}
                                    placeholder="en"
                                    className="w-full p-2 border rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Message Body *
                                </label>
                                <textarea
                                    value={newTemplate.components[0]?.text || ""}
                                    onChange={(e) => updateComponentText(0, e.target.value)}
                                    placeholder="Hello {{1}}, your order #{{2}} is ready for pickup!"
                                    rows={4}
                                    className="w-full p-2 border rounded-md text-sm resize-none"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Use {`{{1}}, {{2}}`} etc. for dynamic variables
                                </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={handleCreateTemplate}
                                    disabled={isLoading}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    ) : (
                                        <Icon name="Plus" className="w-5 h-5" />
                                    )}
                                    Create Template
                                </button>

                                <button
                                    onClick={() => setStep("list")}
                                    className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                    disabled={isLoading}
                                >
                                    Back to Templates
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="pt-4 border-t">
                        <button
                            onClick={onClose}
                            className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}